import { formatValue } from './utils';

interface Change {
	path: string;
	original: string;
	edited: string;
}

export const getChanges = (config: any, editedConfig: any): Change[] => {
	const changes: Change[] = [];

	const compareDeviceConfigWithGlobal = (globalConfig: any, localConfig: any, basePath: string[]) => {
		const platforms = ['android', 'ios', 'com.zupee.free'];

		platforms.forEach((platform) => {
			const globalPlatformConfig = (globalConfig as any)?.[platform];
			const localPlatformConfig = localConfig?.[platform];

			if (!localPlatformConfig) return;

			if (platform === 'com.zupee.free') {
				// Handle nested structure for com.zupee.free
				const subPlatforms = ['android', 'ios'];
				subPlatforms.forEach((subPlatform) => {
					const globalSubConfig = globalPlatformConfig?.[subPlatform];
					const localSubConfig = localPlatformConfig?.[subPlatform];

					if (localSubConfig && JSON.stringify(globalSubConfig) !== JSON.stringify(localSubConfig)) {
						// Only show the specific fields that are different
						Object.keys(localSubConfig).forEach((field) => {
							const globalValue = globalSubConfig?.[field];
							const localValue = localSubConfig[field];

							if (JSON.stringify(globalValue) !== JSON.stringify(localValue)) {
								changes.push({
									path: [...basePath, platform, subPlatform, field].join('.'),
									original: formatValue(globalValue),
									edited: formatValue(localValue)
								});
							}
						});
					}
				});
			} else if (JSON.stringify(globalPlatformConfig) !== JSON.stringify(localPlatformConfig)) {
				// Handle android/ios platforms - only show the specific fields that are different
				Object.keys(localPlatformConfig).forEach((field) => {
					const globalValue = globalPlatformConfig?.[field];
					const localValue = localPlatformConfig[field];

					if (JSON.stringify(globalValue) !== JSON.stringify(localValue)) {
						changes.push({
							path: [...basePath, platform, field].join('.'),
							original: formatValue(globalValue),
							edited: formatValue(localValue)
						});
					}
				});
			}
		});
	};

	const compareObjects = (original: any, edited: any, path: string[] = []) => {
		if (!edited) return;

		// Special handling for deviceConfig - compare against global fallback
		const isDeviceConfigPath = path.length >= 2 && path[path.length - 1] === 'deviceConfig';

		if (isDeviceConfigPath && !original && config?.deviceConfig) {
			// This is a newly created local deviceConfig, compare against global
			const globalDeviceConfig = config.deviceConfig;
			compareDeviceConfigWithGlobal(globalDeviceConfig, edited, path);
			return;
		}

		// Get all keys from both original and edited objects
		const originalKeys = original ? Object.keys(original) : [];
		const editedKeys = Object.keys(edited);
		const allKeys = new Set([...originalKeys, ...editedKeys]);

		allKeys.forEach((key) => {
			const currentPath = [...path, key];
			const originalValue = original?.[key];
			const editedValue = edited[key];

			if (typeof editedValue === 'object' && editedValue !== null && !Array.isArray(editedValue)) {
				compareObjects(originalValue, editedValue, currentPath);
			} else if (JSON.stringify(originalValue) !== JSON.stringify(editedValue)) {
				// Only include meaningful changes (not empty arrays/null to empty arrays/null)
				const isEmptyChange =
					(!originalValue || (Array.isArray(originalValue) && originalValue.length === 0)) &&
					(!editedValue || (Array.isArray(editedValue) && editedValue.length === 0));

				if (!isEmptyChange) {
					changes.push({
						path: currentPath.join('.'),
						original: formatValue(originalValue),
						edited: formatValue(editedValue)
					});
				}
			}
		});
	};

	if (config && editedConfig) {
		compareObjects(config, editedConfig);
	}

	return changes;
};

export const hasChanges = (config: any, editedConfig: any): boolean => {
	return JSON.stringify(config) !== JSON.stringify(editedConfig);
};
